// src/pages/AttendancePage/hooks/useAttendanceData.js
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
	useGetStudentsByDepartmentQuery,
	useGetAbsencesByDepartmentQuery,
	useGetAllClassLogsQuery,
} from "../../../store/api";
import { getStudentDisplayName } from "../../../utils";

export const useAttendanceData = () => {
	const { departmentId } = useParams();

	const {
		data: studentsData = [],
		isLoading: isLoadingStudents,
		error: studentsError,
	} = useGetStudentsByDepartmentQuery(departmentId, {
		skip: !departmentId,
	});

	const {
		data: absencesData = [],
		isLoading: isLoadingAbsences,
		error: absencesError,
	} = useGetAbsencesByDepartmentQuery(departmentId, {
		skip: !departmentId,
	});

	// Fetch all class logs to know when classes were held
	const {
		data: allClassLogs = [],
		isLoading: isLoadingClassLogs,
		error: classLogsError,
	} = useGetAllClassLogsQuery();

	const students = useMemo(() => {
		return studentsData
			.map((student) => ({
				id: student.id,
				name: getStudentDisplayName(student),
				studentId: `STU${(student.studentNumber || student.id)
					.slice(-4)
					.toUpperCase()}`,
			}))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [studentsData]);

	// Process class logs and absences together
	const processedAttendanceData = useMemo(() => {
		const absenceMap = new Map();
		const classLogMap = new Map();

		console.log("Processing all class logs:", allClassLogs);
		console.log("Processing absences data:", absencesData);

		// First, process all class logs for this department to know when classes were held
		const departmentClassLogs = allClassLogs.filter(
			(classLog) => classLog.departmentId === departmentId
		);

		console.log("Filtered class logs for department:", departmentClassLogs);

		departmentClassLogs.forEach((classLog, index) => {
			try {
				console.log(`Processing class log ${index}:`, classLog);

				// Try different possible date field names
				const possibleDateFields = ["date", "Date", "classDate", "logDate"];
				let dateValue = null;

				for (const field of possibleDateFields) {
					if (classLog[field]) {
						dateValue = classLog[field];
						break;
					}
				}

				if (!dateValue) {
					console.warn(`No date found in class log ${index}:`, classLog);
					return;
				}

				const dateObj = new Date(dateValue);
				if (isNaN(dateObj.getTime())) {
					console.warn(`Invalid date in class log ${index}:`, dateValue);
					return;
				}

				const date = dateObj.toISOString().split("T")[0];

				// Try different possible period field names
				const possiblePeriodFields = [
					"period",
					"Period",
					"classPeriod",
					"periodNumber",
				];
				let period = null;

				for (const field of possiblePeriodFields) {
					if (classLog[field] !== undefined && classLog[field] !== null) {
						period = classLog[field];
						break;
					}
				}

				if (!period) {
					console.warn(`No period found in class log ${index}:`, classLog);
					return;
				}

				const dateKey = `${date}-${period}`;

				// Mark that class was held this day/period
				classLogMap.set(dateKey, {
					classLogId: classLog.id,
					subjectId: classLog.subjectId,
					lectureTitle: classLog.lectureTitle,
				});

				console.log(`Added class log to map: ${dateKey}`);
			} catch (error) {
				console.error(`Error processing class log ${index}:`, error, classLog);
			}
		});

		// Then, process absences to know who was absent
		absencesData.forEach((absenceItem, index) => {
			try {
				console.log(`Processing absence item ${index}:`, absenceItem);

				const { absence, classLog } = absenceItem;

				if (!classLog || !absence) {
					console.warn(
						`Missing classLog or absence at index ${index}:`,
						absenceItem
					);
					return;
				}

				// Try different possible date field names
				const possibleDateFields = ["date", "Date", "classDate", "logDate"];
				let dateValue = null;

				for (const field of possibleDateFields) {
					if (classLog[field]) {
						dateValue = classLog[field];
						break;
					}
				}

				// If no date in classLog, try using absenceDate
				if (!dateValue && absence.absenceDate) {
					dateValue = absence.absenceDate;
				}

				if (!dateValue) {
					console.warn(`No date found at index ${index}. ClassLog:`, classLog);
					return;
				}

				const dateObj = new Date(dateValue);
				if (isNaN(dateObj.getTime())) {
					console.warn(`Invalid date at index ${index}:`, dateValue);
					return;
				}

				const date = dateObj.toISOString().split("T")[0];

				// Try different possible period field names
				const possiblePeriodFields = [
					"period",
					"Period",
					"classPeriod",
					"periodNumber",
				];
				let period = null;

				for (const field of possiblePeriodFields) {
					if (classLog[field] !== undefined && classLog[field] !== null) {
						period = classLog[field];
						break;
					}
				}

				const studentId = absence.studentId;

				if (!period || !studentId) {
					console.warn(`Missing period or studentId at index ${index}:`, {
						period,
						studentId,
					});
					return;
				}

				const absenceKey = `${date}-${period}-${studentId}`;

				// Store absence data
				absenceMap.set(absenceKey, {
					isExcused: absence.isExcused,
					reason: absence.reason || "",
					subjectName:
						absenceItem.subjectName ||
						classLog.subjectName ||
						"Unknown Subject",
					absenceId: absence.id,
				});

				console.log(`Added absence to map: ${absenceKey}`);
			} catch (error) {
				console.error(
					`Error processing absence item at index ${index}:`,
					error,
					absenceItem
				);
			}
		});

		console.log("Final absenceMap size:", absenceMap.size);
		console.log("Final classLogMap size:", classLogMap.size);
		console.log("Final absenceMap entries:", Array.from(absenceMap.entries()));
		console.log(
			"Final classLogMap entries:",
			Array.from(classLogMap.entries())
		);

		return { absenceMap, classLogMap };
	}, [absencesData, allClassLogs, departmentId]);

	const isLoading =
		isLoadingStudents || isLoadingAbsences || isLoadingClassLogs;
	const error = studentsError || absencesError || classLogsError;

	return {
		students,
		absenceMap: processedAttendanceData.absenceMap,
		classLogMap: processedAttendanceData.classLogMap,
		isLoading,
		error,
		departmentId,
	};
};
